import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail, Sparkles, Send, Users, Loader2, X, FlaskConical, History, Settings2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { getFunctionErrorMessage } from '@/lib/functionError';
import { paginateQuery } from '@/lib/paginateSupabase';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { BulkCampaignDetailDialog } from '@/components/admin/BulkCampaignDetailDialog';
import { toast } from 'sonner';

const TYPES = [
  { value: 'session_reminder', label: 'Recordatorio de sesión' },
  { value: 'deliverables_reminder', label: 'Recordatorio de entregables' },
  { value: 'general', label: 'Comunicado general' },
  { value: 'progress_summary', label: 'Resumen de avance' },
  { value: 'custom', label: 'Personalizado (con prompt)' },
];
const TYPE_LABEL = Object.fromEntries(TYPES.map((t) => [t.value, t.label]));
const ROLES = [
  { value: 'participant', label: 'Participantes' },
  { value: 'mentor', label: 'Mentores' },
  { value: 'admin', label: 'Administradores de empresa' },
];
const STATUS_LABEL: Record<string, string> = { draft: 'Borrador', sending: 'Enviando', sent: 'Enviado', paused: 'Pausado', failed: 'Falló' };

interface Recipient { user_id: string; email: string; full_name: string | null }

function MultiPick({ items, selected, onChange, placeholder }: {
  items: { id: string; name: string }[]; selected: string[]; onChange: (v: string[]) => void; placeholder: string;
}) {
  const [q, setQ] = useState('');
  const filtered = items.filter((i) => i.name.toLowerCase().includes(q.toLowerCase())).slice(0, 200);
  return (
    <div className="space-y-2">
      <Input placeholder={placeholder} value={q} onChange={(e) => setQ(e.target.value)} />
      <ScrollArea className="h-40 rounded-md border border-border p-2">
        {filtered.map((i) => (
          <label key={i.id} className="flex items-center gap-2 py-1 text-sm cursor-pointer">
            <Checkbox checked={selected.includes(i.id)}
              onCheckedChange={(c) => onChange(c ? [...selected, i.id] : selected.filter((x) => x !== i.id))} />
            <span className="truncate">{i.name}</span>
          </label>
        ))}
        {!filtered.length && <p className="text-sm text-muted-foreground">Sin resultados</p>}
      </ScrollArea>
      {selected.length > 0 && <p className="text-xs text-muted-foreground">{selected.length} seleccionada(s)</p>}
    </div>
  );
}

export default function BulkEmailPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [orgIds, setOrgIds] = useState<string[]>([]);
  const [cohortIds, setCohortIds] = useState<string[]>([]);
  const [roles, setRoles] = useState<string[]>(['participant']);
  const [recipients, setRecipients] = useState<Recipient[] | null>(null);
  const [excluded, setExcluded] = useState<string[]>([]);
  const [loadingRec, setLoadingRec] = useState(false);
  const [emailType, setEmailType] = useState('session_reminder');
  const [instructions, setInstructions] = useState('');
  const [subject, setSubject] = useState('');
  const [htmlBody, setHtmlBody] = useState('');
  const [generating, setGenerating] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [testing, setTesting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [viewId, setViewId] = useState<string | null>(null);

  useEffect(() => { if (user?.email && !testTo) setTestTo(user.email); }, [user?.email]);

  const { data: orgs = [] } = useQuery({
    queryKey: ['bulk-orgs'],
    queryFn: async () => (await supabase.from('organizations').select('id, name').order('name')).data ?? [],
  });
  const { data: cohorts = [] } = useQuery({
    queryKey: ['bulk-cohorts'],
    queryFn: async () => paginateQuery<any>((a, b) =>
      supabase.from('cohorts').select('id, name, organization_id, status, organizations(name)').range(a, b)),
  });
  const cohortItems = useMemo(() => cohorts
    .filter((c: any) => !orgIds.length || orgIds.includes(c.organization_id))
    .filter((c: any) => c.status !== 'archived')
    .map((c: any) => ({ id: c.id, name: `${c.name}${c.organizations?.name ? ' — ' + c.organizations.name : ''}` }))
    .sort((a, b) => a.name.localeCompare(b.name)), [cohorts, orgIds]);

  // Reiniciar destinatarios al cambiar filtros
  useEffect(() => { setRecipients(null); setExcluded([]); }, [orgIds, cohortIds, roles]);

  const { data: campaigns = [] } = useQuery({
    queryKey: ['bulk-campaigns'],
    queryFn: async () => (await supabase.from('bulk_email_campaigns')
      .select('*').order('created_at', { ascending: false }).limit(100)).data ?? [],
    refetchInterval: (q) => ((q.state.data as any[])?.some((c) => c.status === 'sending') ? 4000 : false),
  });
  const { data: creators = {} } = useQuery({
    queryKey: ['bulk-creators', campaigns.map((c: any) => c.created_by).join(',')],
    enabled: campaigns.length > 0,
    queryFn: async () => {
      const ids = [...new Set(campaigns.map((c: any) => c.created_by).filter(Boolean))];
      const { data } = await supabase.from('profiles').select('id, full_name').in('id', ids as string[]);
      return Object.fromEntries((data ?? []).map((p: any) => [p.id, p.full_name]));
    },
  });

  const filters = { organization_ids: orgIds, cohort_ids: cohortIds, roles, excluded_emails: excluded };
  const finalRecipients = (recipients ?? []).filter((r) => !excluded.includes(r.email));

  const loadRecipients = async () => {
    if (!orgIds.length && !cohortIds.length) return toast.error('Selecciona al menos una organización o cohorte.');
    if (!roles.length) return toast.error('Selecciona al menos un tipo de destinatario.');
    setLoadingRec(true);
    const { data, error } = await supabase.functions.invoke('bulk-email-send', {
      body: { mode: 'recipients', filters: { ...filters, excluded_emails: [] } },
    });
    setLoadingRec(false);
    if (error || data?.error) return toast.error(error ? await getFunctionErrorMessage(error) : data.error);
    setRecipients(data.recipients);
  };

  const generate = async () => {
    if (!orgIds.length && !cohortIds.length) return toast.error('Selecciona una organización o cohorte para dar contexto a la IA.');
    setGenerating(true);
    const { data, error } = await supabase.functions.invoke('bulk-email-generate', {
      body: { email_type: emailType, instructions, organization_ids: orgIds, cohort_ids: cohortIds },
    });
    setGenerating(false);
    if (error || data?.error) return toast.error(error ? await getFunctionErrorMessage(error) : data.error);
    setSubject(data.subject); setHtmlBody(data.html_body);
    toast.success('Correo generado. Puedes editarlo antes de enviar.');
  };

  const sendTest = async () => {
    if (!subject || !htmlBody) return toast.error('Primero genera o escribe el correo.');
    setTesting(true);
    const { data, error } = await supabase.functions.invoke('bulk-email-send', {
      body: { mode: 'test', to: testTo, subject, html_body: htmlBody },
    });
    setTesting(false);
    if (error) return toast.error(await getFunctionErrorMessage(error));
    if (data?.error) {
      let detail = '';
      try { detail = JSON.parse(data.details)?.message ?? ''; } catch { /* noop */ }
      return toast.error(data.error, { description: detail || undefined, duration: 12000 });
    }
    toast.success(`Prueba enviada a ${testTo}`);
  };

  const sendCampaign = async () => {
    setSending(true);
    const { data, error } = await supabase.functions.invoke('bulk-email-send', {
      body: { mode: 'campaign', email_type: emailType, prompt: instructions, subject, html_body: htmlBody, filters },
    });
    setSending(false); setConfirmOpen(false);
    if (error || data?.error) return toast.error(error ? await getFunctionErrorMessage(error) : data.error);
    toast.success(`Envío iniciado a ${data.total} personas.`);
    qc.invalidateQueries({ queryKey: ['bulk-campaigns'] });
  };

  const previewHtml = htmlBody.split('{{nombre}}').join((user?.user_metadata?.full_name ?? 'María').split(' ')[0]);
  const viewing = campaigns.find((c: any) => c.id === viewId);

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex items-center gap-3">
        <div className="rounded-xl bg-primary/10 p-2"><Mail className="h-6 w-6 text-primary" /></div>
        <div>
          <h1 className="text-2xl font-bold">Comunicados</h1>
          <p className="text-sm text-muted-foreground">Correos a cohortes u organizaciones, redactados con IA.</p>
        </div>
      </div>

      <Tabs defaultValue="new">
        <TabsList>
          <TabsTrigger value="new"><Send className="mr-2 h-4 w-4" />Nuevo comunicado</TabsTrigger>
          <TabsTrigger value="history"><History className="mr-2 h-4 w-4" />Historial</TabsTrigger>
          <TabsTrigger value="sender"><Settings2 className="mr-2 h-4 w-4" />Remitente</TabsTrigger>
        </TabsList>

        <TabsContent value="new" className="space-y-6">
          <Card>
            <CardHeader><CardTitle>1. Destinatarios</CardTitle>
              <CardDescription>Si eliges cohortes se usan esas; si no, toda la organización.</CardDescription></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div><Label>Organizaciones</Label>
                  <MultiPick items={orgs as any} selected={orgIds} onChange={setOrgIds} placeholder="Buscar organización" /></div>
                <div><Label>Cohortes</Label>
                  <MultiPick items={cohortItems} selected={cohortIds} onChange={setCohortIds} placeholder="Buscar cohorte" /></div>
              </div>
              <div className="flex flex-wrap gap-4">
                {ROLES.map((r) => (
                  <label key={r.value} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={roles.includes(r.value)}
                      onCheckedChange={(c) => setRoles(c ? [...roles, r.value] : roles.filter((x) => x !== r.value))} />
                    {r.label}
                  </label>
                ))}
              </div>
              <Button variant="outline" onClick={loadRecipients} disabled={loadingRec}>
                {loadingRec ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Users className="mr-2 h-4 w-4" />}Ver destinatarios
              </Button>
              {recipients && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">{finalRecipients.length} destinatarios
                    {excluded.length > 0 && <span className="text-muted-foreground"> ({excluded.length} quitados)</span>}</p>
                  <ScrollArea className="h-56 rounded-md border border-border">
                    {recipients.map((r) => {
                      const off = excluded.includes(r.email);
                      return (
                        <div key={r.email} className={`flex items-center justify-between px-3 py-1.5 text-sm ${off ? 'opacity-40 line-through' : ''}`}>
                          <span className="truncate">{r.full_name || '—'} · <span className="text-muted-foreground">{r.email}</span></span>
                          <Button size="icon" variant="ghost" className="h-6 w-6"
                            onClick={() => setExcluded(off ? excluded.filter((e) => e !== r.email) : [...excluded, r.email])}>
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      );
                    })}
                  </ScrollArea>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>2. Tipo de correo y redacción con IA</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div><Label>Tipo</Label>
                  <Select value={emailType} onValueChange={setEmailType}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select></div>
              </div>
              <div><Label>{emailType === 'custom' ? 'Prompt (qué quieres comunicar)' : 'Indicaciones adicionales (opcional)'}</Label>
                <Textarea rows={3} value={instructions} onChange={(e) => setInstructions(e.target.value)}
                  placeholder="Ej.: tono motivador, resalta la importancia de asistir a la mentoría del jueves" /></div>
              <Button onClick={generate} disabled={generating}>
                {generating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
                {subject ? 'Regenerar con IA' : 'Generar con IA'}
              </Button>
              {generating && <p className="text-xs text-muted-foreground">La IA está leyendo la información de la plataforma; puede tardar hasta un minuto.</p>}
              <div><Label>Asunto</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} /></div>
              <div className="grid gap-4 lg:grid-cols-2">
                <div><Label>Contenido (HTML, usa {'{{nombre}}'} para el nombre)</Label>
                  <Textarea rows={16} className="font-mono text-xs" value={htmlBody} onChange={(e) => setHtmlBody(e.target.value)} /></div>
                <div><Label>Vista previa</Label>
                  <div className="h-[340px] overflow-auto rounded-md border border-border bg-card p-4 text-sm [&_a]:text-primary [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_p]:mb-3"
                    dangerouslySetInnerHTML={{ __html: previewHtml }} /></div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>3. Prueba y envío</CardTitle></CardHeader>
            <CardContent className="flex flex-col gap-3 md:flex-row md:items-end">
              <div className="flex-1"><Label>Enviar prueba a</Label>
                <Input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} /></div>
              <Button variant="outline" onClick={sendTest} disabled={testing}>
                {testing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FlaskConical className="mr-2 h-4 w-4" />}Enviar prueba
              </Button>
              <Button onClick={() => setConfirmOpen(true)} disabled={!recipients || !finalRecipients.length || !subject || !htmlBody}>
                <Send className="mr-2 h-4 w-4" />Enviar a {finalRecipients.length || 0}
              </Button>
            </CardContent>
            {!recipients && <p className="px-6 pb-4 text-xs text-muted-foreground">Carga los destinatarios para habilitar el envío.</p>}
          </Card>
        </TabsContent>

        <TabsContent value="history">
          <Card><CardContent className="p-0">
            <div className="divide-y divide-border">
              {campaigns.map((c: any) => (
                <button key={c.id} onClick={() => setViewId(c.id)} className="flex w-full flex-col gap-1 p-4 text-left hover:bg-muted/50 md:flex-row md:items-center md:justify-between">
                  <div className="min-w-0">
                    <p className="truncate font-medium">{c.subject}</p>
                    <p className="text-xs text-muted-foreground">{TYPE_LABEL[c.email_type] ?? c.email_type} · {creators[c.created_by] ?? '—'} · {new Date(c.created_at).toLocaleString('es-CO')}</p>
                    {c.last_error && <p className="text-xs text-destructive truncate">{c.last_error}</p>}
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span>{c.sent_count}/{c.total_recipients} enviados</span>
                    {c.failed_count > 0 && <span className="text-destructive">{c.failed_count} fallidos</span>}
                    <Badge variant={c.status === 'sent' ? 'default' : c.status === 'failed' ? 'destructive' : 'secondary'}>{STATUS_LABEL[c.status]}</Badge>
                  </div>
                </button>
              ))}
              {!campaigns.length && <p className="p-6 text-sm text-muted-foreground">Aún no se han enviado comunicados.</p>}
            </div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="sender"><SenderConfig /></TabsContent>
      </Tabs>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Enviar el comunicado?</AlertDialogTitle>
            <AlertDialogDescription>Se enviará "{subject}" a {finalRecipients.length} personas. Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); sendCampaign(); }} disabled={sending}>
              {sending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Enviar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BulkCampaignDetailDialog campaign={viewing ?? null} onClose={() => setViewId(null)} />
    </div>
  );
}

function SenderConfig() {
  const [cfg, setCfg] = useState({ from_name: 'LÍDER IA', from_email: '', reply_to: '', site_url: 'https://lideria.org' });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    supabase.from('platform_settings').select('value').eq('key', 'bulk_email_config').maybeSingle()
      .then(({ data }) => data?.value && setCfg((c) => ({ ...c, ...(data.value as any) })));
  }, []);
  const save = async () => {
    setSaving(true);
    const { data: existing } = await supabase.from('platform_settings').select('id').eq('key', 'bulk_email_config').maybeSingle();
    const { error } = existing
      ? await supabase.from('platform_settings').update({ value: cfg as any }).eq('key', 'bulk_email_config')
      : await supabase.from('platform_settings').insert({ key: 'bulk_email_config', value: cfg as any });
    setSaving(false);
    error ? toast.error('No se pudo guardar.') : toast.success('Remitente guardado.');
  };
  return (
    <Card>
      <CardHeader><CardTitle>Remitente de los comunicados</CardTitle>
        <CardDescription>El correo remitente debe pertenecer a un dominio verificado en Resend (por ejemplo comunicados.lideria.org). Sin configurarlo, solo podrás enviar pruebas al dueño de la cuenta de Resend.</CardDescription></CardHeader>
      <CardContent className="grid gap-4 md:grid-cols-2">
        <div><Label>Nombre del remitente</Label><Input value={cfg.from_name} onChange={(e) => setCfg({ ...cfg, from_name: e.target.value })} /></div>
        <div><Label>Correo remitente</Label><Input placeholder="hola@comunicados.lideria.org" value={cfg.from_email} onChange={(e) => setCfg({ ...cfg, from_email: e.target.value })} /></div>
        <div><Label>Responder a (opcional)</Label><Input value={cfg.reply_to} onChange={(e) => setCfg({ ...cfg, reply_to: e.target.value })} /></div>
        <div><Label>Dirección de la plataforma</Label><Input value={cfg.site_url} onChange={(e) => setCfg({ ...cfg, site_url: e.target.value })} /></div>
        <div><Button onClick={save} disabled={saving}>{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Guardar</Button></div>
      </CardContent>
    </Card>
  );
}
